import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import SubcontractModal from "./SubcontractModal"
import SubcontractPurchaseInvoicesModal from "./SubcontractPurchaseInvoicesModal"
import {
  signSubcontract, startSubcontract, completeSubcontract,
  cancelSubcontract, destroySubcontract,
} from "../api/subcontracts"
import { fmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const STATUS_TONE = {
  proposed:    "neutral",
  signed:      "blue",
  in_progress: "warning",
  completed:   "success",
  cancelled:   "neutral",
}

const StatTile = ({ label, value, tone }) => (
  <div className={`px-4 py-3 border rounded-xl ${tone || "bg-slate-50 border-slate-200"}`}>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className="text-[18px] font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
)

const SubcontractsSection = ({ subcontracts: initial, policies }) => {
  const projectId = initial?.create_url?.match(/\/projects\/(\d+)\/subcontracts/)?.[1]

  const [items, setItems]               = useState(initial?.items || [])
  const [signedTotal, setSignedTotal]   = useState(initial?.signed_total)
  const [paidTotal,   setPaidTotal]     = useState(initial?.paid_total)
  const [remainingTotal, setRemaining]  = useState(initial?.remaining_total)
  const [modalMode, setModalMode]       = useState(null)
  const [editTarget, setEditTarget]     = useState(null)
  const [piTarget, setPiTarget]         = useState(null)
  const canManage = policies?.can_manage_subcontracts

  const recompute = (next) => {
    const signed = next.filter(it => it.status !== "cancelled" && it.status !== "proposed")
    const signedCents = signed.reduce((acc, it) => acc + (it.contract_amount?.cents || 0), 0)
    const paidCents   = signed.reduce((acc, it) => acc + (it.paid_amount?.cents     || 0), 0)
    const fmtEuros = c => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(c / 100)
    setItems(next)
    setSignedTotal({ cents: signedCents, formatted: fmtEuros(signedCents) })
    setPaidTotal({   cents: paidCents,   formatted: fmtEuros(paidCents)   })
    setRemaining({   cents: signedCents - paidCents, formatted: fmtEuros(signedCents - paidCents) })
  }

  const upsert = (record) => {
    const next = items.some(it => it.id === record.id)
      ? items.map(it => (it.id === record.id ? record : it))
      : [...items, record]
    recompute(next)
  }

  const runTransition = (action, sub) => {
    action(projectId, sub.id).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      upsert(response)
    })
  }

  const handleSign     = (sub) => runTransition(signSubcontract,     sub)
  const handleStart    = (sub) => runTransition(startSubcontract,    sub)
  const handleComplete = (sub) => runTransition(completeSubcontract, sub)
  const handleCancel   = (sub) => {
    if (!confirm(I18n.t("projects.subcontracts.actions.confirm_cancel", { number: sub.number }))) return
    runTransition(cancelSubcontract, sub)
  }

  const handleDelete = (sub) => {
    if (!confirm(I18n.t("projects.subcontracts.actions.confirm_destroy", { number: sub.number }))) return
    destroySubcontract(projectId, sub.id).then(response => {
      if (response?.success) recompute(items.filter(it => it.id !== sub.id))
    })
  }

  const sorted = useMemo(() => [...items].sort((a, b) => a.number.localeCompare(b.number)), [items])

  return (
    <section id="section-subcontracts" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.subcontracts.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.subcontracts.subtitle")}</p>
        </div>
        {canManage && (
          <ActionButton onClick={() => { setEditTarget(null); setModalMode("new") }}>
            {I18n.t("projects.subcontracts.actions.new")}
          </ActionButton>
        )}
      </header>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-3 mb-4">
        <StatTile label={I18n.t("projects.subcontracts.summary.signed_total")}    value={fmt(signedTotal)} />
        <StatTile label={I18n.t("projects.subcontracts.summary.paid_total")}      value={fmt(paidTotal)}   tone="bg-emerald-50/60 border-emerald-200" />
        <StatTile label={I18n.t("projects.subcontracts.summary.remaining_total")} value={fmt(remainingTotal)} tone="bg-amber-50/60 border-amber-200" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[70px_minmax(0,1.4fr)_minmax(0,1.4fr)_120px_120px_120px_120px_130px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
          <div>{I18n.t("projects.subcontracts.table.number")}</div>
          <div>{I18n.t("projects.subcontracts.table.title")}</div>
          <div>{I18n.t("projects.subcontracts.table.supplier")}</div>
          <div className="text-right">{I18n.t("projects.subcontracts.table.amount")}</div>
          <div className="text-right">{I18n.t("projects.subcontracts.table.paid")}</div>
          <div className="text-right">{I18n.t("projects.subcontracts.table.remaining")}</div>
          <div>{I18n.t("projects.subcontracts.table.status")}</div>
          <div className="text-right">{I18n.t("projects.subcontracts.table.actions")}</div>
        </div>

        {sorted.length === 0 ? (
          <div className="px-6 py-10 text-center text-[13px] text-slate-500">
            {I18n.t("projects.subcontracts.empty")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sorted.map(sub => (
              <div
                key       = {sub.id}
                className = "grid grid-cols-[70px_minmax(0,1.4fr)_minmax(0,1.4fr)_120px_120px_120px_120px_130px] gap-3 px-4 py-3 items-center hover:bg-slate-50/60 text-[12.5px]"
              >
                <div className="font-mono text-slate-700">{sub.number}</div>
                <div className="min-w-0">
                  <div className="text-slate-900 font-medium truncate" title={sub.title}>{sub.title}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {sub.bank_guarantee_enabled && (
                      <Badge tone="blue" variant="soft" size="xs">
                        <FontAwesomeIcon icon="shield-halved" className="w-2.5 h-2.5 mr-1" />
                        {I18n.t("projects.subcontracts.badges.bank_guarantee")}
                      </Badge>
                    )}
                    {sub.direct_payment_enabled && (
                      <Badge tone={sub.moa_agreed_at ? "violet" : "warning"} variant="soft" size="xs"
                             title={sub.moa_agreed_at
                               ? I18n.t("projects.subcontracts.badges.moa_agreed", { date: sub.moa_agreed_at, amount: sub.direct_payment_amount?.formatted })
                               : I18n.t("projects.subcontracts.badges.moa_agreement_missing_hint")}>
                        <FontAwesomeIcon icon={sub.moa_agreed_at ? "hand-holding-dollar" : "triangle-exclamation"} className="w-2.5 h-2.5 mr-1" />
                        {I18n.t("projects.subcontracts.badges.direct_payment", { defaultValue: "Paiement direct" })}
                        {!sub.moa_agreed_at && <span className="ml-1 opacity-80">· {I18n.t("projects.subcontracts.badges.moa_agreement_missing")}</span>}
                        {sub.moa_agreed_at && sub.direct_payment_amount_cents > 0 && (
                          <span className="ml-1 tabular-nums opacity-80">· {sub.direct_payment_amount?.formatted}</span>
                        )}
                      </Badge>
                    )}
                    {sub.auto_liquidation_vat && (
                      <Badge tone="amber" variant="soft" size="xs">{I18n.t("projects.subcontracts.badges.auto_vat")}</Badge>
                    )}
                  </div>
                </div>
                <div className="min-w-0 text-slate-700 truncate" title={sub.supplier?.name}>{sub.supplier?.name || "—"}</div>
                <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(sub.contract_amount)}</div>
                <div className="text-right tabular-nums text-emerald-700">{fmt(sub.paid_amount)}</div>
                <div className="text-right tabular-nums text-slate-700">{fmt(sub.remaining_amount)}</div>
                <div>
                  <Badge tone={STATUS_TONE[sub.status] || "neutral"} variant="soft" size="sm">
                    {sub.status_label}
                  </Badge>
                </div>
                <div className="flex items-center justify-end gap-0.5">
                  {canManage && sub.status === "proposed" && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.actions.sign")} onClick={() => handleSign(sub)}>
                      <FontAwesomeIcon icon="signature" />
                    </IconButton>
                  )}
                  {canManage && sub.status === "signed" && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.actions.start")} onClick={() => handleStart(sub)}>
                      <FontAwesomeIcon icon="play" />
                    </IconButton>
                  )}
                  {canManage && (sub.status === "signed" || sub.status === "in_progress") && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.actions.complete")} onClick={() => handleComplete(sub)}>
                      <FontAwesomeIcon icon="flag-checkered" />
                    </IconButton>
                  )}
                  {canManage && sub.status !== "cancelled" && sub.status !== "proposed" && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.purchase_invoices.action")} onClick={() => setPiTarget(sub)}>
                      <FontAwesomeIcon icon="file-invoice" />
                    </IconButton>
                  )}
                  {canManage && sub.status !== "cancelled" && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.actions.edit")} onClick={() => { setEditTarget(sub); setModalMode("edit") }}>
                      <FontAwesomeIcon icon="pen" />
                    </IconButton>
                  )}
                  {canManage && sub.status !== "cancelled" && sub.status !== "completed" && (
                    <IconButton size="md" title={I18n.t("projects.subcontracts.actions.cancel")} onClick={() => handleCancel(sub)}>
                      <FontAwesomeIcon icon="ban" />
                    </IconButton>
                  )}
                  {canManage && sub.status === "proposed" && (
                    <IconButton tone="danger" size="md" title={I18n.t("projects.subcontracts.actions.destroy")} onClick={() => handleDelete(sub)}>
                      <FontAwesomeIcon icon="trash" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SubcontractModal
        isOpen        = {modalMode != null}
        mode          = {modalMode}
        projectId     = {projectId}
        subcontract   = {editTarget}
        suppliersUrl  = {initial?.suppliers_url}
        onClose       = {() => { setModalMode(null); setEditTarget(null) }}
        onSaved       = {upsert}
      />

      {piTarget && (
        <SubcontractPurchaseInvoicesModal
          projectId    = {projectId}
          subcontract  = {piTarget}
          onClose      = {() => setPiTarget(null)}
          onChange     = {(updated) => { upsert(updated); setPiTarget(updated) }}
        />
      )}
    </section>
  )
}

export default SubcontractsSection
